import {
  Button,
  Group,
  useTree,
  Title,
  Table,
  Stack,
  HoverCard,
  Text,
  ThemeIcon,
  Box,
  Switch,
  Tooltip,
  Menu,
} from "@mantine/core"
import { useForm, UseFormReturnType } from "@mantine/form"
import { useClipboard } from "@mantine/hooks"
import {
  IconArrowsSort,
  IconZoomIn,
  IconTrash,
  IconDeviceFloppy,
  IconDownload,
  IconFilePencil,
  IconInfoCircle,
  IconLink,
  IconZoomOut,
} from "@tabler/icons-react"
import _ from "lodash"
import { zodResolver } from "mantine-form-zod-resolver"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { RuleGroupType } from "react-querybuilder"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import { AxesResponses } from "@costory/types/endpoints/axes"
import {
  DigestResponses,
  digestSchema,
  DigestType,
} from "@costory/types/endpoints/digest"
import { DatePresetDigest } from "@costory/types/prisma-client"

import { ICON_SIZE, ICON_STROKE } from "@costory/shared/const"
import {
  generateCostTiles,
  getdeterministicNodeId,
} from "@costory/shared/utils/digest"

import { useUnsavedChangesBlocker } from "@costory/front/components/common/useUnsavedChangesBlocker"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { ExplorerModal } from "@costory/front/pages/DigestV2/DigestModal"
import { DigestNodeRow } from "@costory/front/pages/DigestV2/DigestNodeRow"
import { DigestTiles } from "@costory/front/pages/DigestV2/components/DigestTiles"
import { DigestTitle } from "@costory/front/pages/DigestV2/components/DigestTitle"
import {
  convertToTree,
  formatMarkdown,
  getDefaultRootElem,
  initializeDigest,
  NodeTree,
} from "@costory/front/pages/DigestV2/helper"
import { useAxesQuery } from "@costory/front/queries/axes"
import { useQueryDigestData } from "@costory/front/queries/digest"

const RecursiveAccordion = ({
  nodes,
  isDiffMode,
  exploreInDrawer,
  digest,
  level,
  isOthersAtBottom,
  manuallyCollapsedRows,
  setManuallyCollapsedRows,
  hideOthers = false,
  nodesMap,
  form,
  checkedNodes,
  setCheckedNodes,
  tree,
  costColumn,
  exclusionWhereClause,
  minDiffToHide,
  minRelativeDiffToHide,
}: {
  nodes: NodeTree[]
  isDiffMode: boolean
  exploreInDrawer: (node: NodeTree) => void
  digest: DigestResponses.Investigation
  level: number
  isOthersAtBottom: boolean
  manuallyCollapsedRows: string[]
  setManuallyCollapsedRows: React.Dispatch<React.SetStateAction<string[]>>
  hideOthers?: boolean
  nodesMap: Map<string, NodeTree>
  form: UseFormReturnType<DigestType>
  checkedNodes: Map<string, boolean>
  setCheckedNodes: React.Dispatch<React.SetStateAction<Map<string, boolean>>>
  tree: ReturnType<typeof useTree>
  costColumn: string
  exclusionWhereClause: RuleGroupType
  minDiffToHide: number
  minRelativeDiffToHide: number
}) => {
  const sortedNodes = useMemo(() => {
    if (!isOthersAtBottom) return nodes
    const [notOthers, others] = _.partition(nodes, (n) => !n.isOthers)
    return [...notOthers, ...others]
  }, [nodes, isOthersAtBottom])

  // Filter out nodes with costCurrent as 0
  const filteredNodes = useMemo(() => {
    return sortedNodes.filter((node) => (hideOthers ? !node.isOthers : true))
  }, [sortedNodes, hideOthers])

  return (
    <>
      {filteredNodes.map((node) => {
        const hasActualChildren =
          node.children?.length > 0 &&
          node.children.some((child) => !child.isOthers)
        const isExpanded = !manuallyCollapsedRows.includes(node.value)

        return (
          <>
            <DigestNodeRow
              node={node}
              level={level}
              isExpanded={isExpanded}
              hasActualChildren={hasActualChildren}
              isDiffMode={isDiffMode}
              exploreInDrawer={exploreInDrawer}
              digest={digest}
              setManuallyCollapsedRows={setManuallyCollapsedRows}
              nodesMap={nodesMap}
              form={form}
              checkedNodes={checkedNodes}
              setCheckedNodes={setCheckedNodes}
              tree={tree}
              costColumn={costColumn}
              exclusionWhereClause={exclusionWhereClause}
              minDiffToHide={minDiffToHide}
              minRelativeDiffToHide={minRelativeDiffToHide}
            />
            {/* Only render children if expanded */}
            {hasActualChildren && isExpanded && (
              <RecursiveAccordion
                nodes={node.children}
                isDiffMode={isDiffMode}
                exploreInDrawer={exploreInDrawer}
                digest={digest}
                level={level + 1}
                isOthersAtBottom={
                  node.nodeProps.displayOptions.isOthersAtBottom
                }
                manuallyCollapsedRows={manuallyCollapsedRows}
                setManuallyCollapsedRows={setManuallyCollapsedRows}
                hideOthers={hideOthers}
                nodesMap={nodesMap}
                form={form}
                checkedNodes={checkedNodes}
                setCheckedNodes={setCheckedNodes}
                tree={tree}
                costColumn={costColumn}
                exclusionWhereClause={exclusionWhereClause}
                minDiffToHide={minDiffToHide}
                minRelativeDiffToHide={minRelativeDiffToHide}
              />
            )}
          </>
        )
      })}
    </>
  )
}

type DigestFormProps = {
  initialValues:
    | DigestResponses.Digest
    | { datePreset: DatePresetDigest; period: string }
  onSave: (digest: DigestType) => void
  costColumn: string
  exclusionWhereClause: RuleGroupType
  isEditMode: boolean
  digestConfig: DigestResponses.DigestConfig
}

const treeActionsGuideData = [
  {
    icon: IconZoomIn,
    label: "Zoom Into Line",
    desc: "Split costs by different dimensions (service, team, etc)",
  },
  {
    icon: IconZoomOut,
    label: "Collapse into Others",
    desc: "Hide Item (merge with Others)",
  },
  {
    icon: IconFilePencil,
    label: "Edit Context",
    desc: "See in Explorer Modal",
  },
  {
    icon: IconLink,
    label: "Investigate in Explorer",
    desc: "Opens explorer with the selected node",
  },
  {
    icon: IconTrash,
    label: "Delete All Childs",
    desc: "Delete all child nodes",
  },
  {
    icon: IconArrowsSort,
    label: "Change Ordering",
    desc: "Change the ordering of the nodes",
  },
] as const

const TreeActionsGuideCard = () => (
  <HoverCard
    width={320}
    shadow="md"
    radius={10}
    position="bottom-end"
    data-intercom-target="costory-tour-digest-tree-actions-guide"
  >
    <HoverCard.Target>
      <IconInfoCircle size={ICON_SIZE} stroke={ICON_STROKE} />
    </HoverCard.Target>
    <HoverCard.Dropdown pr={0}>
      <Text fw={600} mb="xs">
        Tree Actions Guide
      </Text>
      <Stack mah={330} pr={10}>
        {treeActionsGuideData.map((item) => (
          <Group key={item.label} wrap="nowrap">
            <ThemeIcon bg="surfaceHover" size={30} radius="md" c="textPrimary">
              <item.icon size={ICON_SIZE} stroke={ICON_STROKE} />
            </ThemeIcon>
            <Stack gap={0}>
              <Text fw={500}>{item.label}</Text>
              <Text size="xs" c="dimmed">
                {item.desc}
              </Text>
            </Stack>
          </Group>
        ))}
      </Stack>
    </HoverCard.Dropdown>
  </HoverCard>
)

const _DigestForm = (
  props: DigestFormProps & {
    axesData: AxesResponses.Axis[]
    digest: DigestResponses.Investigation
    costColumn: string
    exclusionWhereClause: RuleGroupType
    minDiffToHide: number
    minRelativeDiffToHide: number
  },
) => {
  const getDigestData = useMemo(() => {
    const mapper = new Map<string, DigestResponses.Investigation[0]>()
    props.digest.forEach((elem) => {
      mapper.set(getdeterministicNodeId(elem.arr), elem)
    })
    return mapper
  }, [props.digest])
  const defaultItems = [getDefaultRootElem()]
  const isDiffMode = props.initialValues.datePreset != "ABSOLUTE"
  const initialItems: DigestType["items"] =
    "items" in props.initialValues
      ? (props.initialValues.items.map((item) => ({
          ...item,
          labels: item.labels?.map((l) => l.label) || [],
          hiddenItems: item.hiddenItems,
          // Normalize Billy agent sessions from objects to string IDs if needed
          // Backend returns `{ sessionId: string }[]`, while `DigestType` expects `string[]`
          billyAgentSessions: Array.isArray(item.billyAgentSessions)
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
              item.billyAgentSessions.map((session: any) =>
                typeof session === "string" ? session : session.sessionId,
              )
            : [],
        })) as DigestType["items"])
      : (defaultItems as DigestType["items"])
  const [data, setData] = useState<NodeTree[]>(
    convertToTree(initialItems, getDigestData, props.costColumn),
  )
  // Build nodesMap for digest title tooltip
  const nodesMap = React.useMemo(() => {
    const map = new Map<string, NodeTree>()
    function traverse(nodes: NodeTree[]) {
      nodes.forEach((node) => {
        const key = getdeterministicNodeId(node.nodeProps.position)
        // Prioritize parent nodes over Others nodes when they have same deterministic ID
        if (!map.has(key) || (!node.isOthers && map.get(key)?.isOthers)) {
          map.set(key, node)
        }
        if (node.children && node.children.length > 0) {
          traverse(node.children)
        }
      })
    }
    traverse(data)
    return map
  }, [data])
  const form = useForm<DigestType>({
    initialValues: {
      status: "DRAFT",
      name: "New Cost Digest",
      ...props.initialValues,
      items: initialItems,
    } as DigestType,
    validate: zodResolver(digestSchema),
    onValuesChange: (val) => {
      setData(convertToTree(val.items, getDigestData, props.costColumn))
    },
  })

  useUnsavedChangesBlocker({
    isDirtyCallback: () => form.isDirty(),
    onConfirmCallback: () => form.resetDirty(),
    shouldBlockNavigation: ({
      nextLocation,
      currentLocation,
      isDirtyCallback,
    }) => {
      const nodeIdIndex = props.isEditMode ? 4 : 5
      const selectedNodeId = !!(
        nextLocation.split("/")[nodeIdIndex] ||
        currentLocation.split("/")[nodeIdIndex]
      )
      if (selectedNodeId) {
        return false
      }
      return isDirtyCallback()
    },
  })

  useEffect(() => {
    if (!("items" in props.initialValues)) {
      initializeDigest(
        form,
        props.digest,
        props.costColumn,
        props.minDiffToHide,
        props.minRelativeDiffToHide,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [manuallyCollapsedRows, setManuallyCollapsedRows] = useState<string[]>(
    [],
  )
  const [hideOthers, setHideOthers] = useState(false)
  const navigate = useNavigate()
  const baseUrl =
    "id" in props.initialValues && props.isEditMode
      ? `digests/edit/${props.initialValues.id}`
      : `digests/new/${props.initialValues.period}/${props.initialValues.datePreset ?? "LAST_MONTH"}`
  const location = useLocation()
  const exploreInModal = (node: NodeTree) => {
    navigate(
      `${location.pathname}/${getdeterministicNodeId(node.nodeProps.position)}?mode=preview`,
    )
  }
  const clipboard = useClipboard({ timeout: 500 })

  const tree = useTree({
    initialExpandedState:
      "items" in props.initialValues
        ? { [props.initialValues.items[0].id]: true }
        : undefined,
  })
  const [checkedNodes, setCheckedNodes] = useState<Map<string, boolean>>(
    new Map(),
  )

  const nodesWithVisualContent = useMemo(() => {
    const result: string[] = []
    const traverse = (nodes: NodeTree[]) => {
      nodes.forEach(({ nodeProps, value, children }) => {
        // Check for images in imgUrl property
        const hasNote = nodeProps.note?.trim()
        const badges = hasNote
          ? hasNote
              .split("\n")
              .filter((l) => l.trim().startsWith("####"))
              .join("\n")
          : ""

        // Check for visual content in notes (markdown images)
        const hasVisualContentInNote = hasNote && hasNote !== badges

        if (hasVisualContentInNote) {
          result.push(value)
        }
        if (children?.length) traverse(children)
      })
    }
    traverse(data)
    return result
  }, [data])
  const toggleAllNodes = useCallback(() => {
    setManuallyCollapsedRows((prev) => {
      // If no nodes are collapsed, collapse all nodes with visual content
      // If some nodes are collapsed, expand all nodes
      return prev.length === 0 ? nodesWithVisualContent : []
    })
  }, [nodesWithVisualContent])

  const tiles = generateCostTiles(
    props.digest,
    form.values.period,
    props.costColumn,
  )
  const { selectedNodeId } = useParams()

  const selectedNodeDrawer = useMemo(() => {
    return selectedNodeId ? nodesMap.get(selectedNodeId) || data[0] : data[0]
  }, [selectedNodeId, nodesMap, data])

  return (
    <>
      <DigestTitle form={form} digestData={props.digest} isDiff={isDiffMode} />
      <DigestTiles
        tiles={tiles}
        costColumn={props.costColumn}
        period={form.values.period}
        hideAbsoluteDiffTiles={[tiles[1].label]}
        exclusionWhereClause={props.exclusionWhereClause}
      />
      <Group mt={20} justify="space-between">
        <Title order={3} mb="md">
          Cost Breakdown Tree
        </Title>
        <Group mr={5}>
          <Tooltip
            label={
              nodesWithVisualContent.length === 0
                ? "No nodes found"
                : manuallyCollapsedRows.length === 0
                  ? "Collapse all nodes"
                  : "Expand all nodes"
            }
          >
            <Button
              variant="subtle"
              size="xs"
              onClick={toggleAllNodes}
              disabled={nodesWithVisualContent.length === 0}
              aria-label={
                manuallyCollapsedRows.length === 0
                  ? "Collapse all nodes"
                  : "Expand all nodes"
              }
              data-intercom-target="costory-tour-digest-collapse-expand"
            >
              {nodesWithVisualContent.length === 0
                ? "No nodes"
                : manuallyCollapsedRows.length === 0
                  ? "Collapse all"
                  : "Expand all"}
            </Button>
          </Tooltip>
          <Switch
            label="Hide Others"
            checked={hideOthers}
            onChange={(event) => setHideOthers(event.currentTarget.checked)}
            size="md"
            data-intercom-target="costory-tour-digest-hide-others"
          />
          <Menu shadow="sm" width={250}>
            <Menu.Target>
              <Button
                aria-label="Export"
                leftSection={<IconDownload size={ICON_SIZE} />}
                variant={clipboard.copied ? undefined : "outline"}
                color={clipboard.copied ? "teal" : undefined}
              >
                Export
              </Button>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                onClick={() =>
                  clipboard.copy(
                    formatMarkdown(
                      form,
                      props.axesData,
                      getDigestData,
                      "2024-12-01",
                      props.costColumn,
                      baseUrl,
                      false,
                    ),
                  )
                }
                data-intercom-target="costory-tour-digest-export-markdown"
              >
                Export to Markdown (clipboard)
              </Menu.Item>

              {/* <Menu.Item
                disabled
                onClick={() =>
                  clipboard.copy(
                    formatMarkdown(
                      form,
                      props.axesData,
                      getDigestData,
                      "2024-12-01",
                      props.costColumn,
                      baseUrl,
                      true,
                    ),
                  )
                }
                data-intercom-target="costory-tour-digest-export-table"
              >
                Export as Table (clipboard)
              </Menu.Item> */}
            </Menu.Dropdown>
          </Menu>
          <Button
            aria-label="Save digest"
            leftSection={<IconDeviceFloppy size={ICON_SIZE} />}
            onClick={() => {
              props.onSave(form.getValues())
              form.resetDirty()
            }}
            disabled={form.getInputProps("name").value.trim().length === 0}
            data-intercom-target="costory-tour-digest-save"
          >
            Save digest
          </Button>
          <TreeActionsGuideCard />
        </Group>
      </Group>
      <ExplorerModal
        selectedNodeDrawer={selectedNodeDrawer}
        period={
          props.initialValues.datePreset === "LAST_MONTH"
            ? "MonthToMonth"
            : "MTD"
        }
        digestForm={form}
        setManuallyCollapsedRows={setManuallyCollapsedRows}
        costColumn={props.costColumn}
        nodesMap={nodesMap}
        exclusionWhereClause={props.exclusionWhereClause}
        digest={props.digest}
        isEditMode={props.isEditMode}
      />
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w="40%">Name</Table.Th>
            <Table.Th w="42%" ta="center">
              Context
            </Table.Th>
            <Table.Th w="8%" ta="right">
              {props.costColumn === "contracted_cost"
                ? "Contracted Cost"
                : "Cost"}
            </Table.Th>
            <Table.Th w="8%" ta="right">
              Change
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
      </Table>
      <Box py={10}>
        <RecursiveAccordion
          nodes={data}
          isDiffMode={isDiffMode}
          exploreInDrawer={exploreInModal}
          digest={props.digest}
          level={0}
          isOthersAtBottom={data[0].nodeProps.displayOptions.isOthersAtBottom}
          manuallyCollapsedRows={manuallyCollapsedRows}
          setManuallyCollapsedRows={setManuallyCollapsedRows}
          hideOthers={hideOthers}
          nodesMap={nodesMap}
          form={form}
          checkedNodes={checkedNodes}
          setCheckedNodes={setCheckedNodes}
          tree={tree}
          costColumn={props.costColumn}
          exclusionWhereClause={props.exclusionWhereClause}
          minDiffToHide={props.minDiffToHide}
          minRelativeDiffToHide={props.minRelativeDiffToHide}
        />
      </Box>
    </>
  )
}

export const DigestForm = (props: DigestFormProps) => {
  const query = useQueryDigestData(
    (props.initialValues.datePreset || "LAST_MONTH") as DatePresetDigest,
    props.initialValues.period,
    "id" in props.initialValues ? props.initialValues.id : undefined,
  )
  const axes = useAxesQuery()
  const minDiffToHide = props.digestConfig?.minDiffThreshold ?? 100
  const minRelativeDiffToHide =
    props.digestConfig?.relativeDiffThreshold ?? 0.01
  return (
    <>
      <QueryWrapper query={axes}>
        {(axesData) => (
          <>
            <QueryWrapper query={query}>
              {(digest) => (
                <_DigestForm
                  {...props}
                  digest={digest.data}
                  axesData={axesData.data}
                  costColumn={props.costColumn}
                  exclusionWhereClause={props.exclusionWhereClause}
                  minDiffToHide={minDiffToHide}
                  minRelativeDiffToHide={minRelativeDiffToHide}
                />
              )}
            </QueryWrapper>
          </>
        )}
      </QueryWrapper>
    </>
  )
}
